import {Stack, Text, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useEffect, useRef, useState} from 'react';

import type {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import type {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {getPlateMapWithWellEdits} from 'samples/plate-maps/PlateMapUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInputWithAction from 'ui-library/inputs/NumericInputWithAction';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {HydratedUnit} from 'units/util';
import {NumericInputChangeEvent} from 'util/Types';

const DEBOUNCE_MS = 50;

export interface WellVolumeSettingsProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
}

export default function WellVolumeSettings({
  plateMap,
  setPlateMap,
  selectedWellIndices,
  deselectWells,
}: WellVolumeSettingsProps): JSX.Element {
  const {classes} = useStyles();
  const volumeInputId = useId();

  // Local controlled state. value='' means "no value typed yet" — we won't overwrite existing well data.
  const [value, setValue] = useState<number | string>('');
  const [unit, setUnit] = useState<MeasurementUnit | null>(() => {
    // Prepopulate with the shared unit across the initial selection (if all wells agree).
    const indices = [...selectedWellIndices];
    if (indices.length === 0) {
      return null;
    }
    const firstUnit = plateMap.wells[indices[0]]?.quantity?.unit ?? null;
    const allAgree = indices.every(
      (i) => (plateMap.wells[i]?.quantity?.unit?.id ?? null) === (firstUnit?.id ?? null)
    );
    return allAgree ? firstUnit : null;
  });

  // Debounced auto-apply — mirrors AutoApplyConcentrationLayerSettings.
  // plateMap lives in a ref so the effect only fires when value/unit/selection actually change.
  const plateMapRef = useRef(plateMap);
  plateMapRef.current = plateMap;

  const unitRef = useRef(unit);
  unitRef.current = unit;

  useEffect(() => {
    // Don't write until the user has specified both a numeric value and unit — avoids overwriting existing
    // wells on initial selection or when only a value or unit has been populated
    const numericValue = typeof value === 'number' ? value : null;
    if (numericValue == null || selectedWellIndices.size === 0) {
      return;
    }
    if (unitRef.current == null) {
      return;
    }
    const timer = setTimeout(() => {
      const edits: {[index: number]: Partial<PlateMap['wells'][number]>} = {};
      for (const idx of selectedWellIndices) {
        edits[idx] = {quantity: {value: numericValue, unit: unitRef.current}};
      }
      setPlateMap(getPlateMapWithWellEdits(plateMapRef.current, edits));
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
    // Leave plateMap and setPlateMap out of deps — only fire when value/unit/selection change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, unit, selectedWellIndices]);

  function handleValueChange(e: NumericInputChangeEvent): void {
    setValue(e.target.numericValue ?? '');
  }

  function handleUnitSelect(selected: HydratedUnit | null): void {
    // HydratedUnit is structurally compatible with MeasurementUnit for the fields we store
    setUnit(selected as MeasurementUnit | null);
  }

  const noWellsSelected = selectedWellIndices.size === 0;

  return (
    <Stack spacing="sm" {...getTestProps({component: 'WellVolumeSettings', element: 'root'})}>
      <Text size="md" className={classes.instructionText}>
        Select wells in the plate to set the max volume.
      </Text>
      <div>
        <FieldLabel label="Units " isRequired={true} />
        <SearchableUnitDropdown
          selectedId={unit?.id}
          unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[MeasurementType.VOLUME]}
          displaySelectionAsSymbol={true}
          onSelect={handleUnitSelect}
        />
      </div>
      <div>
        <FieldLabel label="Max volume " htmlFor={volumeInputId} isRequired={true} />
        <NumericInputWithAction
          id={volumeInputId}
          value={value}
          min={0}
          onChange={handleValueChange}
          validateInteger={false}
          required={true}
          action={{
            label: 'Submit',
            disabled: noWellsSelected,
            onClick: deselectWells,
          }}
        />
      </div>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  instructionText: {
    color: theme.colors.gray[7],
  },
}));
